import { useEffect, useMemo, useState, type ReactNode } from "react";
import { GAMES } from "../config";
import { inviteDeadlineMs, type ClientInvite } from "../game/invite-clock";
import { ConfirmDialog } from "./ConfirmDialog";
import { faDigits } from "../i18n/digits";
import { faApiError } from "../i18n/errors";
import { fa } from "../i18n/fa";
import { useSession, useSessionStore } from "../store/context";
import { displayName } from "./profile-ui";
import { AvatarRing } from "./social-ui";
import { useCountdown } from "./useCountdown";

/**
 * App-level incoming game-invite prompt — rendered once at the root (next to
 * `FriendToast`) so it overlays any view, including a live table. Shows who
 * invited, to which game, and a live countdown off the invite's 60s TTL, with
 * پیوستن / رد. Accept goes through the store (`acceptGameInvite`), which joins
 * the table via the existing join-by-roomId path; a dead invite (409) shows its
 * Persian reason inline before auto-dismissing. Expiry auto-dismisses.
 *
 * It is also the missed-while-offline sweep: on session-ready it pulls
 * `GET /invites/incoming` once and prompts the newest live invite.
 */
/**
 * The longest countdown that still means something on this card, in seconds.
 *
 * Comfortably above the longest table window a player can actually be waiting on (a public fill
 * countdown is 45s; a private host's own turn/settings never gate an invite), and far below the
 * `GAME_INVITE_MAX_TTL_MS` ceiling an invite carries while its table is not counting at all.
 */
const COUNTDOWN_MAX_SEC = 90;

/**
 * HOW OFTEN AN OPEN PROMPT RE-READS ITS OWN INVITE — ADAPTIVE, see {@link INVITE_POLL_FAST_MS}.
 *
 * The deadline belongs to the TABLE, not to this invite: the API derives it from the table's
 * published countdown, so it appears when a second human seats, and disappears again if that
 * human leaves and the countdown pauses. The invitee is not at that table and nothing pushes them
 * the change — so the card asks.
 *
 * This is the cadence used WHILE A COUNTDOWN IS ON SCREEN, where a re-read adds very little: the
 * chip already ticks off the anchored deadline, and a table that keeps counting runs at the same
 * one second per second, so the number is right without asking. The only thing a poll can add here
 * is a PAUSE — and asking every couple of seconds would also re-anchor the visible digit against
 * the round trip on every answer, which makes it jitter.
 */
export const INVITE_POLL_MS = 10_000;

/**
 * THE CADENCE WHILE THE CARD SHOWS NO COUNTDOWN — the only state a window can ARM in.
 *
 * THE BUG THIS EXISTS TO KILL (measured on device, 2026-08-27): the poll was a flat
 * {@link INVITE_POLL_MS} `setInterval`, which fires first at +10s and never at 0 — so an arm that
 * landed while the card was open was invisible for 0–10 seconds BY CONSTRUCTION. On the reported
 * table the chip appeared at ~35s of a 45s countdown: the friend lost the first nine seconds of
 * the very window they were being asked to answer in.
 *
 * A ceiling-state card renders NO number, so a fast re-read has nothing to make jitter, and it is
 * cheap where it counts: an invite is answered in a median 4.8s (measured on prod, 3 days /
 * 4,578 answered), so most cards are gone before even this fires once, and at the busiest hour
 * measured (3.7 invites/min) the whole platform holds well under one open card at a time.
 *
 * ⚠️ A SUSPENDED WEBVIEW MAKES NO INTERVAL LENGTH RIGHT, AND THAT IS FINE. Telegram's WebView does
 * not throttle a background timer, it STOPS it: measured in Chromium (frozen page: zero ticks in
 * 12s, at BOTH cadences) and on real devices in our own ledger (a `reconnect-gave-up` beacon with
 * `attempts=1 elapsedMs=8,498,514 hiddenMs=0 appActive=true` — an 8s abort timer that never fired
 * in 2h21m). What matters is that an overdue interval fires promptly on resume — 651ms, measured —
 * so a poll SELF-HEALS where a pushed event, published while the socket was dead, would simply be
 * lost. That is why this is a re-read and not a push.
 */
export const INVITE_POLL_FAST_MS = 2_000;

export function InvitePrompt(): ReactNode {
  const invite = useSession((s) => s.invitePrompt);
  const status = useSession((s) => s.status);
  const refreshInvites = useSession((s) => s.refreshInvites);
  const promptNextIncomingInvite = useSession((s) => s.promptNextIncomingInvite);

  useEffect(() => {
    if (status !== "ready") return;
    void refreshInvites().then(promptNextIncomingInvite);
  }, [status, refreshInvites, promptNextIncomingInvite]);

  if (!invite) return null;
  // Keyed remount per invite so countdown/error state never leaks across invites.
  return <InviteCard key={invite.id} invite={invite} />;
}

function InviteCard({ invite }: { invite: ClientInvite }): ReactNode {
  const acceptGameInvite = useSession((s) => s.acceptGameInvite);
  const declineGameInvite = useSession((s) => s.declineGameInvite);
  const dismiss = useSession((s) => s.dismissInvitePrompt);
  const view = useSession((s) => s.view);
  const online = useSession((s) => s.online);
  const beginGameSwitch = useSession((s) => s.beginGameSwitch);
  const clearGameSwitch = useSession((s) => s.clearGameSwitch);
  // The deadline on THIS device's clock, fixed for the life of the card (the component is keyed
  // per invite, so a new invite gets a fresh one). Anchored on arrival — never re-derived from the
  // server's `expiresAt`, which is meaningless against a phone whose clock disagrees.
  const deadlineMs = useMemo(() => inviteDeadlineMs(invite), [invite]);
  const secondsLeft = useCountdown(deadlineMs);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The server no longer lists this invite (its table dealt, or it was withdrawn while we were
   *  away): render it as expired rather than leaving a button that can only fail. */
  const [gone, setGone] = useState(false);
  /**
   * HAS THE SERVER AGREED THAT THIS LAPSE IS REAL? — see {@link confirmLapse}.
   *
   * A lapsed deadline used to BE the verdict. It cannot be any more: the deadline belongs to the
   * TABLE and now moves in BOTH directions — it appears when a second human seats and GOES AWAY
   * AGAIN when they leave and the countdown pauses, handing the invite back its long ceiling. So a
   * card holding a nearly-spent window can watch it run out at the very moment the server gave the
   * time back, and the number on screen is the only thing that says otherwise.
   */
  const [lapseConfirmed, setLapseConfirmed] = useState(false);
  const refreshInvites = useSession((s) => s.refreshInvites);
  const store = useSessionStore();
  /** The anchored window has run out — a SUSPICION until {@link confirmLapse} settles it. */
  const lapsed = secondsLeft === 0;
  /**
   * IS A COUNTDOWN ACTUALLY ON SCREEN? Drives both the chip and the poll cadence, from ONE
   * expression, so «no chip» and «poll fast» can never drift apart.
   *
   * ⚠️ A BOOLEAN, NEVER `secondsLeft` ITSELF, IN THE POLL'S DEPS. The seconds change every tick;
   * an effect keyed on them would clear and re-arm its interval once a second and therefore NEVER
   * fire. This flips only at the two edges (the ceiling falling under the cap, and zero).
   */
  const counting = secondsLeft > 0 && secondsLeft <= COUNTDOWN_MAX_SEC;
  const expired = ((lapsed && lapseConfirmed) || gone) && !error;
  const gameFa = GAMES.find((g) => g.id === invite.game)?.fa ?? invite.game;
  // Am I already IN a game? Accepting then means LEAVING it — confirm first, then an animated switch.
  const inGame = view === "game";
  /**
   * SEATED AT A LIVE PUBLIC TABLE ⇒ THIS INVITE IS NOT ACCEPTABLE AT ALL (operator, 2026-08-27).
   *
   * Accepting used to take the ORDINARY resumable exit out of the public game — no loss, seat
   * reclaimable, leave counter untouched — so an invite was a free way out of a game that was
   * going badly. The chosen fix is to refuse the accept, NOT to penalise it: being invited is not
   * something to punish anyone for. The card still arrives and still names the inviter and the
   * game; it withholds «پیوستن» and says why, and keeps «رد» so the friend gets a real answer
   * instead of a window that simply runs out.
   *
   * ⚠️ THE FLAG MEANS «LEAVING RIGHT NOW COSTS A LOSS», nothing wider — false in a waiting room,
   * on a private table, on the win screen and in solo, all of which stay freely switchable.
   */
  const blockedByPublicGame = useSession((s) => s.publicGameInProgress);

  /**
   * A DEADLINE THAT MOVED BACK UP MUST NOT BE READ AS DEATH — ask the server before saying so.
   *
   * THE BUG THIS EXISTS TO KILL: the poll below is gated on `expired`, and `expired` was
   * `secondsLeft === 0`. So when the table's second human left inside the last poll gap, the
   * server reverted this invite to its ceiling — and the card, whose anchored window ran out
   * first, tore its own poll down, said «این دعوت منقضی شد» and auto-dismissed. Measured against
   * this component: at t+8.1s the notice was up and پیوستن was gone, at t+11s the card was out of
   * the DOM, and by t+41s exactly ONE fetch had ever been made while the store still listed the
   * invite as live. The friend then had no way back to it at all — `promptNextIncomingInvite` only
   * runs on session-ready — so a host who had done nothing wrong had to invite again.
   *
   * ONE re-read on the zero edge settles it: absent ⇒ the server really let go (the `gone` path);
   * still listed with a deadline that has genuinely passed ⇒ confirmed. Anything else means the
   * window moved, and `refreshInvites` has already re-seated this card with the fresher one, so
   * `deadlineMs` — and with it `secondsLeft` — simply comes back to life.
   *
   * ⚠️ FAILS TOWARD THE OLD BEHAVIOUR ON PURPOSE. `refreshInvites` swallows its own errors and
   * never rejects, so a dead network settles this with the store untouched: the invite is still
   * listed carrying the deadline that just lapsed, and the card expires exactly as it used to.
   *
   * ⚠️ NEVER WHILE BUSY — the poll's own rule, for the same reason: an accept flips the row out of
   * the live list, and a check landing mid-tap would read that as death.
   */
  useEffect(() => {
    if (!lapsed || lapseConfirmed || gone || error || busy) return;
    let alive = true;
    void refreshInvites().then(() => {
      if (!alive) return;
      const fresh = store.getState().incomingInvites.find((i) => i.id === invite.id);
      if (!fresh) setGone(true);
      else if (inviteDeadlineMs(fresh) <= Date.now()) setLapseConfirmed(true);
    });
    return () => {
      alive = false;
    };
  }, [lapsed, lapseConfirmed, gone, error, busy, refreshInvites, store, invite.id]);

  /** DERIVED, NEVER LATCHED: a fresher deadline from ANY source (this check, the poll, the push
   *  handler's own refetch) re-opens the question rather than leaving a confirmed lapse standing. */
  useEffect(() => {
    setLapseConfirmed(false);
  }, [deadlineMs]);

  // The TTL lapsed while showing (or a dead-invite error was shown): hold a
  // beat so the notice is readable, then auto-dismiss.
  useEffect(() => {
    if (!expired && !error && !gone) return;
    const id = window.setTimeout(dismiss, 2500);
    return () => window.clearTimeout(id);
  }, [expired, error, gone, dismiss]);

  // RE-READ THIS INVITE WHILE THE CARD IS OPEN — see {@link INVITE_POLL_MS}.
  //
  // `refreshInvites` re-anchors the open prompt against whatever the server now says, so a window
  // that opened, moved or was given back reaches the card by itself. And when the invite is no
  // longer listed at all — its table dealt, or it was withdrawn while we were offline — say so
  // rather than leaving a «پیوستن» that can only 409.
  //
  // ⚠️ NEVER WHILE BUSY. An accept flips the row out of the live list the moment it succeeds, so
  // a poll landing mid-tap would declare the invite dead underneath a player who is already
  // walking into the table.
  useEffect(() => {
    if (expired || error || gone) return;
    const id = window.setInterval(
      () => {
        if (busy) return;
        void refreshInvites().then(() => {
          if (!store.getState().incomingInvites.some((i) => i.id === invite.id)) setGone(true);
        });
      },
      counting ? INVITE_POLL_MS : INVITE_POLL_FAST_MS,
    );
    return () => window.clearInterval(id);
  }, [expired, error, gone, busy, counting, refreshInvites, store, invite.id]);

  // Do the accept + raise the switch overlay. The invited game's `OnlineGame` clears the overlay
  // once it reaches its REAL phase (waiting / انتخاب یار و حریف / the table) — never the lobby.
  const doAccept = async (): Promise<void> => {
    setBusy(true);
    beginGameSwitch(
      !inGame
        ? fa.invite.switchingLobby
        : online
          ? fa.invite.switchingOnline // leaving an online game: your seat → bot
          : fa.invite.switchingSolo, // leaving a solo game: it's saved + resumable
    );
    try {
      // Success navigates into the invited table (joinPrivate); the overlay covers the connect.
      await acceptGameInvite(invite.id);
    } catch (err) {
      clearGameSwitch(); // accept failed (expired/decided) → drop the overlay + show the reason
      setError(faApiError(err));
      setBusy(false);
    }
  };

  // In a game → confirm the leave first; otherwise accept straight away.
  const accept = (): void => {
    if (inGame) setConfirming(true);
    else void doAccept();
  };

  const decline = async (): Promise<void> => {
    setBusy(true);
    try {
      await declineGameInvite(invite.id); // success drops the row + the prompt
    } catch (err) {
      setError(faApiError(err));
      setBusy(false);
    }
  };

  return (
    <>
      <div
        dir="rtl"
        role="alertdialog"
        aria-label={fa.invite.incoming(inviterName(invite), gameFa)}
        className="fixed inset-x-0 z-[55] mx-auto flex w-[calc(100%-32px)] max-w-[400px] flex-col gap-3 rounded-xl border border-gold-deep/60 bg-surface-container-highest p-md shadow-2xl"
        // Sits below the FriendToast (z-60) and clears the status bar / Dynamic
        // Island exactly like it, but a step lower so both stay readable together.
        // bg-pop = the app's shared overlay entrance (no abrupt mount).
        style={{
          top: "calc(72px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
          animation: "bg-pop 0.3s ease",
        }}
      >
        {/* NO game banner (operator, 2026-07-23). The cover said nothing the text does not — the
            prompt is about WHO is inviting you and to WHICH game, and both are already words a
            line below. Removing it also makes the notification the compact card it should be. */}
        <div className="flex items-center gap-sm">
          <AvatarRing
            avatarId={invite.from.avatarId}
            avatarPhotoId={invite.from.avatarPhotoId}
            size={44}
            showDot={false}
          />
          <p className="m-0 min-w-0 flex-1 font-body-md text-body-md text-on-surface">
            {fa.invite.incoming(inviterName(invite), gameFa)}
          </p>
          {/* Live countdown off the anchored device-clock deadline — the table's own remaining
              window, one second short of the deal.

              HIDDEN ABOVE {@link COUNTDOWN_MAX_SEC}: a public table counts nothing until a SECOND
              human sits down, so an invite from a lone host is created with the long ceiling
              instead of a table deadline (there is none yet to sync to). A «۶۰۰ ثانیه» chip would
              read as noise rather than urgency — and, worse, as a promise about a clock that is
              not running. It reappears the moment the host's table starts counting and the server
              shortens the invite to that window. */}
          {counting ? (
            <span
              className={`flex shrink-0 items-center gap-1 rounded-full border px-2 py-1 font-label-md text-label-md ${
                expired
                  ? "border-outline-variant/50 text-on-surface-variant"
                  : "border-gold-deep/50 text-secondary"
              }`}
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {faDigits(secondsLeft)} {fa.invite.seconds}
            </span>
          ) : null}
        </div>

        {error ? (
          <p role="alert" className="m-0 text-center font-body-md text-body-md text-error">
            {error}
          </p>
        ) : expired ? (
          <p className="m-0 text-center font-body-md text-body-md text-on-surface-variant">
            {fa.invite.expiredNotice}
          </p>
        ) : blockedByPublicGame ? (
          /* Not acceptable while a public game is in progress — say so, and leave only «رد». */
          <div className="flex flex-col gap-2">
            <p className="m-0 text-center font-body-md text-body-md text-on-surface-variant">
              {fa.invite.blockedPublic}
            </p>
            <p className="m-0 text-center font-caption text-caption text-on-surface-variant">
              {fa.invite.blockedPublicWhy}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void decline()}
              className="cursor-pointer appearance-none rounded-lg border border-outline-variant bg-surface-container py-2.5 font-body-md text-body-md text-on-surface-variant transition-colors hover:text-on-surface active:scale-95 disabled:opacity-60 disabled:active:scale-100"
            >
              {fa.invite.decline}
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={accept}
              className="flex-1 cursor-pointer appearance-none rounded-lg border-0 bg-gold-deep py-2.5 font-body-md text-body-md font-bold text-ink-on-gold shadow-md transition-colors hover:bg-secondary active:scale-95 disabled:opacity-60 disabled:active:scale-100"
            >
              {fa.invite.accept}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void decline()}
              className="flex-1 cursor-pointer appearance-none rounded-lg border border-outline-variant bg-surface-container py-2.5 font-body-md text-body-md text-on-surface-variant transition-colors hover:text-on-surface active:scale-95 disabled:opacity-60 disabled:active:scale-100"
            >
              {fa.invite.decline}
            </button>
          </div>
        )}
      </div>
      {confirming ? (
        /* Accepting while IN a game leaves it — confirm first. Wording depends on the current game:
         an online game hands your seat to a bot; a solo game is saved + resumable. */
        <ConfirmDialog
          title={fa.invite.switchTitle}
          body={online ? fa.invite.switchBodyOnline : fa.invite.switchBodySolo}
          confirmLabel={fa.invite.switchConfirm}
          cancelLabel={fa.invite.switchCancel}
          onConfirm={() => {
            setConfirming(false);
            void doAccept();
          }}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </>
  );
}

/** The inviter's display name — the one shared resolution (`displayName`). */
function inviterName(invite: ClientInvite): string {
  return displayName(invite.from);
}
